import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import NotFoundPage from '@/pages/NotFoundPage';
import SolutionFlag from '@/components/solutions/SolutionFlag';
import { getSolution, solutionCountries, solutionPath } from '@/data/solutions/registry';
import { iconFor } from '@/data/solutions/icons';
const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={`sol-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid={`sol-faq-toggle-${index}`}
      >
        <span className="text-sm sm:text-base font-semibold text-slate-900">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-5 pb-5 text-sm sm:text-base text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
};
const DEFAULT_STATUS_FILTERS = [
  { key: 'all', label: 'All statuses' },
  { key: 'mandatory', label: 'Required' },
  { key: 'upcoming', label: 'Starting soon' },
  { key: 'voluntary', label: 'Voluntary or limited' },
];
const statusKey = (status) => (status === 'upcoming' ? 'upcoming' : status === 'voluntary' ? 'voluntary' : 'mandatory');
const statusDot = (status) => (status === 'upcoming' ? 'bg-amber-400' : status === 'voluntary' ? 'bg-slate-400' : 'bg-[#0066CC]');
const chipClass = (active) =>
  `text-sm font-semibold px-4 py-1.5 rounded-full transition-colors ${
    active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400'
  }`;
const SolutionHub = ({ s }) => {
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const all = solutionCountries(s.slug);
  const hasCountries = all.length > 0;
  const regions = s.regions ? s.regions.filter((r) => all.some((c) => c.region === r)) : Array.from(new Set(all.map((c) => c.region).filter(Boolean)));
  const statusFilters = all.length > 0 ? (s.statusFilters || DEFAULT_STATUS_FILTERS) : [];
  const visible = all.filter(
    (c) => (regionFilter === 'all' || c.region === regionFilter) && (statusFilter === 'all' || statusKey(c.status) === statusFilter)
  );
  const path = solutionPath(s.slug);
  const hero = s.hero;
  // Page-level structured data (FAQ + breadcrumb). Removed when leaving the page.
  useEffect(() => {
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: s.faq.items.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://taxera.tech/' },
          { '@type': 'ListItem', position: 2, name: 'Solutions', item: 'https://taxera.tech/products' },
          { '@type': 'ListItem', position: 3, name: s.name, item: `https://taxera.tech${path}` },
        ],
      },
    ];
    const nodes = schemas.map((schema, i) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-page-jsonld', `sol-${i}`);
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });
    return () => nodes.forEach((n) => n.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.slug]);
  return (
    <PageLayout>
      <div data-testid={`sol-page-${s.slug}`}>
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 pt-10 pb-16">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-400 mb-8" data-testid="sol-breadcrumb">
            <Link to="/" className="hover:text-slate-700">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-slate-700">Solutions</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-600">{s.name}</span>
          </nav>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#14A3B8] mb-4">{s.name}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] max-w-4xl">
              {hero.h1}
              {hero.h1Accent && (
                <>
                  {' '}
                  <span className="text-[#0066CC]">{hero.h1Accent}</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">{hero.text}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#0066CC] hover:bg-[#0052A3] text-white font-semibold px-6 py-3 rounded-xl transition-colors"
                data-testid="sol-cta-demo"
              >
                Book a demo <ArrowRight className="w-4 h-4" />
              </Link>
              {hasCountries && (
                <a
                  href="#countries"
                  className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0066CC] text-slate-800 font-semibold px-6 py-3 rounded-xl transition-colors"
                  data-testid="sol-cta-countries"
                >
                  {hero.secondaryCta || 'See requirements by country'}
                </a>
              )}
              {hero.externalCta && (
                <a
                  href={hero.externalCta.url}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0066CC] text-slate-800 font-semibold px-6 py-3 rounded-xl transition-colors"
                  data-testid="sol-cta-external"
                >
                  {hero.externalCta.label} <ArrowRight className="w-4 h-4" />
                </a>
              )}
            </div>
          </motion.div>
        </section>
        {/* Requirements by country */}
        {hasCountries && (
          <section id="countries" className="bg-slate-50 border-y border-slate-100 scroll-mt-24" data-testid="sol-countries">
            <div className="max-w-6xl mx-auto px-6 py-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{s.countriesSection.heading}</h2>
              <p className="text-slate-600 mb-8 max-w-2xl">{s.countriesSection.intro}</p>
              {regions.length > 1 && (
                <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Filter by region" data-testid="sol-region-filter">
                  <button type="button" onClick={() => setRegionFilter('all')} aria-pressed={regionFilter === 'all'} className={chipClass(regionFilter === 'all')} data-testid="sol-region-all">
                    All regions
                  </button>
                  {regions.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRegionFilter(r)}
                      aria-pressed={regionFilter === r}
                      className={chipClass(regionFilter === r)}
                      data-testid={`sol-region-${r.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              )}
              <div className="mb-6 flex flex-wrap gap-2" role="group" aria-label="Filter by status" data-testid="sol-status-filter">
                {statusFilters.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setStatusFilter(f.key)}
                    aria-pressed={statusFilter === f.key}
                    className={chipClass(statusFilter === f.key)}
                    data-testid={`sol-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {all.length > 0 && visible.length === 0 && <p className="text-slate-500 py-6" data-testid="sol-no-results">No countries match these filters yet.</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {visible.map((c) => (
                  <Link
                    key={c.slug}
                    to={`${path}/${c.slug}`}
                    className="group relative flex flex-col justify-between min-h-[190px] rounded-3xl bg-white border border-slate-200 p-6 hover:border-[#0066CC] hover:shadow-lg hover:shadow-blue-900/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0066CC] transition-all"
                    data-testid={`sol-country-link-${c.slug}`}
                  >
                    <div className="flex items-start justify-between">
                      <SolutionFlag code={c.code} name={c.name} className="h-14 w-14" />
                      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-400 group-hover:bg-[#0066CC] group-hover:border-[#0066CC] group-hover:text-white transition-colors">
                        <ArrowRight className="w-4 h-4 -rotate-45 group-hover:rotate-0 transition-transform" />
                      </span>
                    </div>
                    <div className="mt-8">
                      <div className="font-heading text-2xl font-semibold text-slate-900 leading-tight">{c.name}</div>
                      <div className="mt-1 text-sm text-slate-500">{c.shortName || ''}</div>
                      <div className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                        <span className={`h-2 w-2 rounded-full ${statusDot(c.status)}`} aria-hidden="true" />
                        {c.statusLabel}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              {s.countriesSection.trackerPath && (
                <p className="mt-8 text-sm text-slate-500">
                  Want to follow changes as they happen? See the{' '}
                  <Link to={s.countriesSection.trackerPath} className="text-[#0066CC] font-semibold hover:underline" data-testid="sol-tracker-link">
                    Global Tax Tracker
                  </Link>
                  .
                </p>
              )}
            </div>
          </section>
        )}
        {/* What it is */}
        <section className="bg-slate-50 border-y border-slate-100" data-testid="sol-what">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{s.what.heading}</h2>
              </div>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                {s.what.paragraphs.map((p) => (
                  <p key={p.slice(0, 40)}>{p}</p>
                ))}
              </div>
            </div>
            {s.what.explainer && (
              <>
                <h3 className="mt-12 mb-6 text-lg font-bold text-slate-900" data-testid="sol-explainer">{s.what.explainer.heading}</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {s.what.explainer.items.map((m, i) => {
                    const Icon = iconFor(m.icon);
                    return (
                      <div key={m.title} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`sol-explainer-${i + 1}`}>
                        <span className="w-9 h-9 rounded-lg bg-[#0066CC]/10 flex items-center justify-center mb-4">
                          <Icon className="w-5 h-5 text-[#0066CC]" strokeWidth={1.5} />
                        </span>
                        <h4 className="text-base font-bold text-slate-900 mb-2">{m.title}</h4>
                        <p className="text-sm text-slate-600 leading-relaxed">{m.text}</p>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </section>
        {/* Apps / features (pages without country guides) */}
        {s.apps && (
          <section className="max-w-6xl mx-auto px-6 py-16" data-testid="sol-apps">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{s.apps.heading}</h2>
            <p className="text-slate-600 mb-10 max-w-2xl">{s.apps.intro}</p>
            <div className="grid sm:grid-cols-2 gap-5">
              {s.apps.items.map((a, i) => {
                const Icon = iconFor(a.icon);
                return (
                  <div key={a.title} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`sol-app-${i + 1}`}>
                    <span className="w-10 h-10 rounded-lg bg-[#0066CC]/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-[#0066CC]" strokeWidth={1.5} />
                    </span>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{a.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{a.text}</p>
                    {a.bullets && (
                      <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
                        {a.bullets.map((b) => (
                          <li key={b} className="flex gap-2">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#14A3B8] shrink-0" />
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
        {/* How it works */}
        <section className={s.apps ? 'bg-slate-50 border-y border-slate-100' : 'max-w-6xl mx-auto px-6 py-16'} data-testid="sol-how">
          <div className={s.apps ? 'max-w-6xl mx-auto px-6 py-16' : undefined}>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{s.steps.heading}</h2>
            <p className="text-slate-600 mb-10 max-w-2xl">{s.steps.intro}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {s.steps.items.map((st, i) => {
                const Icon = iconFor(st.icon);
                return (
                  <motion.div
                    key={st.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.07 }}
                    className="rounded-2xl border border-slate-200 bg-white p-6"
                    data-testid={`sol-step-${i + 1}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-9 h-9 rounded-lg bg-[#0066CC]/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#0066CC]" strokeWidth={1.5} />
                      </span>
                      <span className="text-xs font-semibold text-slate-400">STEP {i + 1}</span>
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2">{st.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{st.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
        {/* Benefits */}
        <section className={s.apps ? 'max-w-6xl mx-auto px-6 py-16' : 'bg-slate-50 border-y border-slate-100'} data-testid="sol-benefits">
          <div className={s.apps ? undefined : 'max-w-6xl mx-auto px-6 py-16'}>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-10">{s.benefits.heading}</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {s.benefits.items.map((b, i) => {
                const Icon = iconFor(b.icon);
                return (
                  <motion.div
                    key={b.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.07 }}
                    className="flex gap-4 rounded-2xl bg-white border border-slate-200 p-6"
                    data-testid={`sol-benefit-${i + 1}`}
                  >
                    <span className="w-10 h-10 shrink-0 rounded-lg bg-[#14A3B8]/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#14A3B8]" strokeWidth={1.5} />
                    </span>
                    <div>
                      <h3 className="text-base font-bold text-slate-900 mb-1">{b.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{b.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-6 py-16" data-testid="sol-faq">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">{s.faq.heading}</h2>
          <div className="space-y-3">
            {s.faq.items.map((f, i) => (
              <FaqItem key={f.q} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </section>
        {/* Related */}
        <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="sol-related">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Keep reading</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {s.related.map((r) => {
              const external = /^https?:\/\//.test(r.to);
              const cardClass = 'group rounded-xl border border-slate-200 p-5 hover:border-[#0066CC]/50 hover:shadow-md transition-all';
              const testId = `sol-related-${r.to.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}`;
              const inner = (
                <>
                  <div className="text-sm font-semibold text-slate-900 group-hover:text-[#0066CC] transition-colors">{r.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{r.note}</div>
                </>
              );
              return external ? (
                <a key={r.to} href={r.to} target="_blank" rel="noopener" className={cardClass} data-testid={testId}>
                  {inner}
                </a>
              ) : (
                <Link key={r.to} to={r.to} className={cardClass} data-testid={testId}>
                  {inner}
                </Link>
              );
            })}
          </div>
        </section>
        {/* Final CTA */}
        <section className="bg-[#1A1A1A] text-white" data-testid="sol-final-cta">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold">{s.cta.heading}</h2>
            <p className="mt-3 text-slate-300 max-w-xl mx-auto">{s.cta.text}</p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 bg-[#0066CC] hover:bg-[#0052A3] text-white font-semibold px-7 py-3 rounded-xl transition-colors"
              data-testid="sol-final-demo"
            >
              Book a demo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};
const SolutionHubPage = () => {
  const { product } = useParams();
  const s = getSolution(product);
  if (!s) return <NotFoundPage />;
  return <SolutionHub s={s} key={s.slug} />;
};
export default SolutionHubPage;
